import { type PortalMember } from "@/hooks/useUsers";
import { filterUsers } from "@/lib/userFilters";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { Plus, Upload, User } from "lucide-react";
import { useState } from "react";

import { SectionHeader } from "./SectionHeader";
import { UserRow } from "./UserRow";

export function UsersSection({
  users,
  currentUserId,
  onEditUser,
  onRemoveUser,
  onAddUser,
  onUploadCsv,
}: {
  users: PortalMember[];
  currentUserId: string | undefined;
  onEditUser: (u: PortalMember) => void;
  onRemoveUser: (userId: string) => void;
  onAddUser: () => void;
  onUploadCsv: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  const filteredUsers = filterUsers(users, searchQuery, roleFilter);

  return (
    <section className="mb-10">
      <SectionHeader
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        icon={<User className="h-5 w-5" />}
        title="Users"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onUploadCsv}>
              <Upload className="h-4 w-4" /> Upload CSV
            </Button>
            <Button size="sm" onClick={onAddUser}>
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
        }
      />
      {!collapsed && (
        <>
          <div className="mb-3 flex gap-3">
            <Input
              size="sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="flex-1"
            />
            <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v)}>
              <SelectTrigger size="sm" className="w-auto" aria-label="Filter by role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
                <SelectItem value="viewer">Viewers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filteredUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users yet.</p>
          ) : (
            <div className="border border-border rounded divide-y divide-border">
              {filteredUsers.map((u) => (
                <UserRow
                  key={u.userId}
                  user={u}
                  isCurrentUser={u.userId === currentUserId}
                  onEdit={() => onEditUser(u)}
                  onRemove={() => onRemoveUser(u.userId)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
